import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import {
  approveMaster,
  activateMaster,
  createMaster,
  listMaster,
  proposeMasterChange,
  purgeMaster,
  rejectMaster,
  retireMaster,
  updateMasterDirect,
  type MasterListParams,
  type MasterListResult,
  type ProposeChangeResult,
} from '@/lib/api-masters';
import type { NormalizationRule, NormalizationRuleCreateInput } from './types';

const KEY = 'normalization-rules' as const;

export const useNormalizationRulesList = (params: MasterListParams = {}) => {
  return useQuery<MasterListResult<NormalizationRule>, ApiClientError>({
    queryKey: [KEY, 'list', params],
    queryFn: () => listMaster<NormalizationRule>('normalization-rules', params),
    placeholderData: (prev) => prev,
  });
};

export const useCreateNormalizationRule = () => {
  const qc = useQueryClient();
  return useMutation<NormalizationRule, ApiClientError, NormalizationRuleCreateInput>({
    mutationFn: (body) =>
      createMaster<NormalizationRule, NormalizationRuleCreateInput>('normalization-rules', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useApproveNormalizationRule = () => {
  const qc = useQueryClient();
  return useMutation<NormalizationRule, ApiClientError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) =>
      approveMaster<NormalizationRule>('normalization-rules', id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useRejectNormalizationRule = () => {
  const qc = useQueryClient();
  return useMutation<NormalizationRule, ApiClientError, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) =>
      rejectMaster<NormalizationRule>('normalization-rules', id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useRetireNormalizationRule = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => retireMaster('normalization-rules', id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useActivateNormalizationRule = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => activateMaster('normalization-rules', id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const usePurgeNormalizationRule = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => purgeMaster('normalization-rules', id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

/**
 * Manager-only direct edit. `PUT /api/masters/normalization/:id`.
 *
 * Note: list/approve/reject still use the legacy `normalization-rules`
 * slug; the change-proposal endpoints use `normalization` per the
 * backend contract.
 */
export const useUpdateNormalizationRuleDirect = () => {
  const qc = useQueryClient();
  return useMutation<
    NormalizationRule,
    ApiClientError,
    { id: string; body: Partial<NormalizationRuleCreateInput> }
  >({
    mutationFn: ({ id, body }) =>
      updateMasterDirect<NormalizationRule, Partial<NormalizationRuleCreateInput>>(
        'normalization-rules',
        id,
        body,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

/** Supervisor+ change-proposal. */
export const useProposeNormalizationRuleChange = () => {
  const qc = useQueryClient();
  return useMutation<
    ProposeChangeResult,
    ApiClientError,
    { id: string; body: { proposed: Partial<NormalizationRuleCreateInput>; reason: string } }
  >({
    mutationFn: ({ id, body }) =>
      proposeMasterChange<Partial<NormalizationRuleCreateInput>>('normalization-rules', id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};
